import React from 'react';
import { observer } from 'mobx-react-lite';
import { X, Clock, Trash2 } from 'lucide-react';
import TimePicker from '../ui/time-picker';
import Button from '../ui/button';
import { useAvailabilityViewModel } from '../../view-model/professional/availability-view-model';
import { useTranslation } from 'react-i18next';

// Interface pour les créneaux horaires
interface TimeSlot {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  isRecurring: boolean;
  hasBookings?: boolean;
  dayOfWeek?: string;
  serviceId?: string;
  serviceName?: string;
}

interface TimeSlotEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  timeSlot: TimeSlot | null;
}

const TimeSlotEditModal: React.FC<TimeSlotEditModalProps> = ({
  isOpen,
  onClose,
  timeSlot
}) => {
  const viewModel = useAvailabilityViewModel();
  const { t } = useTranslation();
  
  if (!isOpen || !timeSlot) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto mx-4 sm:mx-0 my-4 sm:my-0">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-semibold">
            {timeSlot.isRecurring ? t('availability.editSlot1', 'Modifier un créneau récurrent') : t('availability.editSlot2', 'Modifier un créneau')}
          </h2>
          <button 
            onClick={onClose}
            className="rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Date - affichage en lecture seule */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('availability.date', 'Date')}
            </label>
            <div className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300">
              {new Date(viewModel.editDate).toLocaleDateString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          </div>
          
          {/* Message d'avertissement si le créneau a des réservations */}
          {timeSlot?.hasBookings && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-orange-700 dark:text-orange-300 text-sm font-medium">
                  ⚠️ {t('availability.slotHasBookings', 'Ce créneau a des réservations')}
                </span>
              </div>
              <p className="text-orange-600 dark:text-orange-400 text-xs mt-1">
                {t('availability.cannotModifyWithBookings', 'Vous ne pouvez pas modifier les heures ou supprimer ce créneau car il contient des réservations.')}
              </p>
            </div>
          )}

          {/* Heures */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('availability.startTime', 'Heure de début')}
              </label>
              {timeSlot?.hasBookings ? (
                <div className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{viewModel.editStartTime}</span>
                  <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded ml-auto">
                    {t('availability.hasBookings', 'Réservé')}
                  </span>
                </div>
              ) : (
                <TimePicker
                  name={t('availability.startTime', 'Heure de début')}
                  value={viewModel.editStartTime}
                  onChange={(e) => viewModel.setEditStartTime(e.target.value)}
                  withButton
                  icon={<Clock className="h-4 w-4" />}
                  className="w-full"
                />
              )}
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('availability.endTime', 'Heure de fin')}
              </label>
              {timeSlot?.hasBookings ? (
                <div className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{viewModel.editEndTime}</span>
                </div>
              ) : (
                <TimePicker
                  name={t('availability.endTime', 'Heure de fin')}
                  value={viewModel.editEndTime}
                  onChange={(e) => viewModel.setEditEndTime(e.target.value)}
                  withButton
                  icon={<Clock className="h-4 w-4" />}
                  className="w-full"
                />
              )}
            </div>
          </div>
          {viewModel.getError && <div className="flex text-red-700 justify-center">{viewModel.getError}</div>}
          {/* Boutons */}
          <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-0 sm:space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* Bouton de suppression visible uniquement sur mobile */}
            <Button
              onClick={() => {
                if (timeSlot?.hasBookings) {
                  alert(t('availability.cannotDeleteWithBookings', 'Impossible de supprimer un créneau avec des réservations'));
                  return;
                }
                onClose();
                viewModel.setIsConfirmDialogOpen(timeSlot.id);
              }}
              variant="primary"
              className={`sm:hidden w-full mb-2 flex items-center justify-center ${
                timeSlot?.hasBookings
                  ? 'bg-gray-400 cursor-not-allowed opacity-50'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              disabled={timeSlot?.hasBookings}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('availability.delete', 'Supprimer')}
            </Button>

            <div className="flex sm:flex-row flex-col w-full sm:w-auto gap-2 sm:gap-0 sm:space-x-3">
              <Button
                onClick={onClose}
                variant="secondary"
                className="w-full sm:w-auto"
              >
                {t('availability.cancel', 'Annuler')}
              </Button>
              <Button
                onClick={() => {
                  if (timeSlot?.hasBookings) {
                    alert(t('availability.cannotModifyWithBookings', 'Impossible de modifier un créneau avec des réservations'));
                    return;
                  }
                  viewModel.updateTimeSlot();
                }}
                variant="primary"
                className={`w-full sm:w-auto ${
                  timeSlot?.hasBookings ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={timeSlot?.hasBookings}
              >
                {t('availability.save', 'Enregistrer')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(TimeSlotEditModal);
